import React, { useState, useMemo } from "react";
import { Row, Col } from "reactstrap";
import ComponentCard from "./ComponentCard";
import '../../index.css';
import { useTable, usePagination, useSortBy } from 'react-table';
import '../../AppTable.css';
import {
  Box,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { FormatItalic } from "@mui/icons-material";

const authService = require("../../service/AuthService");

const VendorPriceTable = () => {
  const [startDate, setStartDate] = useState("2025-01-01");
  const [endDate, setEndDate] = useState("2025-01-15");
  const [aggregateBy, setAggregateBy] = useState("");
  const [data, setData] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [progressLabel, setProgressLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [committedStartDate, setCommittedStartDate] = useState(startDate);
  const [committedEndDate, setCommittedEndDate] = useState(endDate);
  const [committedAggregateBy, setCommittedAggregateBy] = useState(aggregateBy);


  const vendorPricingUrl = 'https://mcqjfq0lkl.execute-api.us-east-1.amazonaws.com/test/get_competitor_pricing';

  const fetchData = async () => {
    setLoading(true);
    setErrorMsg("");

    setCommittedStartDate(startDate);
    setCommittedEndDate(endDate);
    setCommittedAggregateBy(aggregateBy);

    const payload = {
      action: "fetch",
      parameters: [
        {
          start_date: startDate,
          end_date: endDate,
          aggregate_by: aggregateBy || null,
        },
      ],
    };

    try {
      const res = await fetch(vendorPricingUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const result = await res.json();
      const parsed = JSON.parse(result.body);
      setData(parsed);
      setHasFetched(true);
      setErrorMsg("");
    } catch (err) {
      setErrorMsg(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return null;
    const dt = Math.floor(dateTime).toString();
    return `${dt.slice(0, 4)}-${dt.slice(4, 6)}-${dt.slice(6, 8)} ${dt.slice(8, 10)}:${dt.slice(10, 12)}`;
  };

  const capitalize = (str) =>
    typeof str === "string" ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

  const columns = useMemo(() => [
    {
      Header: 'Date Time',
      accessor: row => formatDateTime(row.date_time),
      disableSortBy: false
    },
    {
      Header: 'Product Title',
      accessor: 'product_title',
      disableSortBy: true,
      Cell: ({ row }) => (
        <a
          href={row.original.product_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            minWidth: "180px",
            whiteSpace: "normal",
            wordBreak: "break-word",
            display: "inline-block"
          }}>
          {row.original.product_title}
        </a>
      )
    },
    {
      Header: 'Vendor Name',
      accessor: row => capitalize(row.vendor_name),
      id: 'vendor_name'
    },
    {
      Header: 'Brand',
      accessor: row => capitalize(row.brand),
      id: 'brand'
    },
    { Header: 'Color', accessor: 'color' },
    { Header: 'Quantity', accessor: 'quantity', sortType: 'basic' },
    { Header: 'Grade', accessor: 'grade' },
    { Header: 'Price', accessor: 'price', sortType: 'number' },
    { Header: 'Price Per Unit', accessor: 'price_per_unit', sortType: 'number' }
  ], []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    gotoPage,
    pageCount,
    state: { pageIndex }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 50 }
    },
    useSortBy,
    usePagination
  );

  const renderTable = () => {
    if (loading) {
        return (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <CircularProgress color="primary" />
          </div>
        );
      }
      
    if (!hasFetched) return null;

    if (committedAggregateBy === "brand") {
      return (
        <table className="app-table">
          <thead>
            <tr>
              <th>Brand</th>
              <th>Min Price Per Unit</th>
              <th>Max Price Per Unit</th>
              <th>Avg Price Per Unit</th>
              <th>Min Price Vendor</th>
              <th>Max Price Vendor</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => (
              <tr key={idx}>
                <td>{capitalize(item.brand)}</td>
                <td>{item.min_price_per_unit}</td>
                <td>{item.max_price_per_unit}</td>
                <td>{item.avg_price_per_unit}</td>
                <td>{capitalize(item.min_price_vendor)}</td>
                <td>{capitalize(item.max_price_vendor)}</td>
              </tr>
            ))}2
          </tbody>
        </table>
      );
    }

    if (committedAggregateBy === "vendor") {
      return (
        <table className="app-table">
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Min Price Per Unit</th>
              <th>Max Price Per Unit</th>
              <th>Avg Price Per Unit</th>
              <th>Min Price Brand</th>
              <th>Max Price Brand</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => (
              <tr key={idx}>
                <td>{capitalize(item.vendor_name)}</td>
                <td>{item.min_price_per_unit}</td>
                <td>{item.max_price_per_unit}</td>
                <td>{item.avg_price_per_unit}</td>
                <td>{capitalize(item.min_price_brand)}</td>
                <td>{capitalize(item.max_price_brand)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return (
      <>
      {pageCount > 1 && (
      <div className="pagination-controls" style={{ marginTop: "10px", textAlign: "center" }}>
          <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</Button>
          <Button onClick={previousPage} disabled={!canPreviousPage}>Previous</Button>
          <Button onClick={nextPage} disabled={!canNextPage}>Next</Button>
          <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{">>"}</Button>
          <span style={{ marginLeft: "1rem" }}>
            Page <strong>{pageIndex + 1} of {pageCount}</strong>
          </span>
        </div>
        )}
        <table {...getTableProps()} className="app-table">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())} style={{ whiteSpace: "nowrap", cursor: "pointer" }}>
                    {column.render('Header')}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? " 🔽"
                          : " 🔼"
                        : ""}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, idx) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        {pageCount > 1 && (
        <div className="pagination-controls" style={{ marginTop: "10px", textAlign: "center" }}>
          <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</Button>
          <Button onClick={previousPage} disabled={!canPreviousPage}>Previous</Button>
          <Button onClick={nextPage} disabled={!canNextPage}>Next</Button>
          <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{">>"}</Button>
          <span style={{ marginLeft: "1rem" }}>
            Page <strong>{pageIndex + 1} of {pageCount}</strong>
          </span>
        </div>
        )}
      </>
    );
  };

  return (
    <div className="customer-page" style={{ padding: '20px 0', marginTop: "10px" }}>
        <div
            style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '20px',
                    boxSizing: 'border-box',
                    marginTop: '80px',
                  }}>
            <Typography variant="h6" gutterBottom align="center">
                <strong>Vendor Pricing Dashboard</strong>
            </Typography>
            <Typography variant="h6" gutterBottom align="center">
                Product: Used Golf Balls
            </Typography>
        </div>
        <Row className="justify-content-center" style={{ margin: "0 auto", maxWidth: "1200px" }}>
          <Col sm="12" md="12">
            <ComponentCard>
                <Row style={{ paddingTop: "0px", marginBottom: "20px" }}>
                {progressLabel && <div style={{ color: "green" }}>{progressLabel}</div>}
                <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        size="small"
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        size="small"
                    />
                    <TextField
                        select
                        label="Aggregate By"
                        value={aggregateBy}
                        onChange={(e) => setAggregateBy(e.target.value)}
                        sx={{ minWidth: 200 }}
                        size="small"
                    >
                        <MenuItem value="raw">Detailed View</MenuItem>
                        <MenuItem value="brand">Brand</MenuItem>
                        <MenuItem value="vendor">Vendor</MenuItem>
                    </TextField>
                    <Button 
                            variant="contained" 
                            onClick={fetchData}
                            style = {{ backgroundColor: '#f0f0f0' , color: 'grey', textTransform: 'none', fontSize: '16px' }}
                            >
                        Fetch Data
                        </Button>
                    </Stack>

                    {errorMsg && <Typography color="error" mb={2}>{errorMsg}</Typography>}
                    <Row style={{ paddingTop: "30px" }}></Row>
                    <Box sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {renderTable()}
                    </Box>
                </CardContent>
                </Row>
            </ComponentCard>
            </Col>
        </Row>
    </div>
  );
};

export default VendorPriceTable;
